/**
 * OnboardingEglise — gate affiché aux users connectés sans église.
 * Crée leur première église puis redirige vers le Dashboard.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import { authApi } from "../services/api";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import Logo from "../components/Logo";
import Input from "../components/Input";
import Select from "../components/Select";
import Button from "../components/Button";

const PAYS = [
  "Côte d'Ivoire", "Sénégal", "Burkina Faso", "Mali", "Bénin", "Togo",
  "Cameroun", "Gabon", "Tchad", "Congo (RDC)", "Congo (Brazzaville)",
  "France", "Belgique", "Suisse", "Canada", "Haïti", "Madagascar", "Maurice", "Autre",
];

const DEVISES = [
  { value: "EUR", label: "Euro (€)" },
  { value: "XOF", label: "FCFA (XOF)" },
  { value: "XAF", label: "FCFA (XAF)" },
  { value: "CAD", label: "Dollar canadien" },
  { value: "CHF", label: "Franc suisse" },
];

export default function OnboardingEglise() {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    eglise_nom: "",
    eglise_ville: "",
    eglise_pays: "Côte d'Ivoire",
    eglise_devise: "XOF",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: null }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const errs = {};
    if (!form.eglise_nom.trim() || form.eglise_nom.trim().length < 2) errs.eglise_nom = "Nom requis";
    if (!form.eglise_ville.trim()) errs.eglise_ville = "Ville requise";
    if (!form.eglise_pays) errs.eglise_pays = "Pays requis";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    setGlobalError(null);
    try {
      const { data } = await authApi.onboarding({
        eglise_nom: form.eglise_nom.trim(),
        eglise_ville: form.eglise_ville.trim(),
        eglise_pays: form.eglise_pays,
        eglise_devise: form.eglise_devise,
      });
      updateUser(data);
      toast.success(`✓ Église « ${form.eglise_nom} » créée !`);
      navigate("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la création de l'église";
      setGlobalError(typeof msg === "string" ? msg : "Erreur");
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `radial-gradient(circle at 15% 0%, rgba(228,167,44,0.10) 0%, transparent 50%), ${colors.bg}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: fonts.body,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: "20px",
          padding: "36px 32px",
          boxShadow: "0 25px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <Logo size="md" showName />
        </div>

        <h2 style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, marginBottom: "6px" }}>
          👋 Bienvenue {user?.full_name?.split(" ")[0] || ""} !
        </h2>
        <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "20px" }}>
          Avant de commencer, dites-nous quelque chose sur votre église.
          Elle sera créée et prête à utiliser dans la minute.
        </p>

        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
            marginBottom: "16px",
          }}>
            {globalError}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Input
            label="Nom de l'église"
            value={form.eglise_nom}
            onChange={set("eglise_nom")}
            placeholder="Ex : Église de la Grâce"
            required
            error={errors.eglise_nom}
            icon="⛪"
            autoFocus
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Input
              label="Ville"
              value={form.eglise_ville}
              onChange={set("eglise_ville")}
              placeholder="Abidjan"
              required
              error={errors.eglise_ville}
            />
            <Select
              label="Pays"
              value={form.eglise_pays}
              onChange={set("eglise_pays")}
              options={PAYS.map((p) => ({ value: p, label: p }))}
              required
              placeholder={null}
            />
          </div>
          <Select
            label="Devise"
            value={form.eglise_devise}
            onChange={set("eglise_devise")}
            options={DEVISES}
            placeholder={null}
          />
          <Button type="submit" fullWidth size="lg" loading={submitting} disabled={submitting} style={{ marginTop: "8px" }}>
            Créer mon église et commencer →
          </Button>
        </form>
      </div>
    </div>
  );
}
